/** @jsxImportSource @emotion/react */
import { createStyles, IconName, NavbarItem } from '@quickbit/qb-design-system';
import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useSession } from '../context/Session';
import { formatRoutePath, hasPermissions } from '../utils';

export interface Tab {
  name: string;
  icon: IconName;
  component: ReactNode;
  subTabs?: Tab[];
  requiredPermissions?: string[] | string[][];
}

interface Props {
  tab: Tab;
}

export const Tab = ({ tab }: Props) => {
  const styles = useStyles();
  const session = useSession();
  const userPermissions = session.authenticated ? session.permissions : [];

  const allowedSubTabs = tab.subTabs?.filter(({ requiredPermissions }) =>
    hasPermissions(userPermissions, requiredPermissions)
  );

  if (
    (!allowedSubTabs?.length && !tab.component) ||
    !hasPermissions(userPermissions, tab.requiredPermissions)
  ) {
    return null;
  }

  const path = `/${formatRoutePath(tab.name)}`;

  return (
    <>
      <NavLink to={path} replace={true} style={{ textDecoration: 'none' }}>
        {({ isActive }: { isActive: boolean }) => (
          <NavbarItem icon={tab.icon} name={tab.name} isActive={isActive} />
        )}
      </NavLink>
      {allowedSubTabs && (
        <div css={styles.subTabs}>
          {allowedSubTabs.map((subTab) => (
            <NavLink
              key={subTab.name}
              to={`${path}/${formatRoutePath(subTab.name)}`}
              replace={true}
              style={{ textDecoration: 'none' }}
            >
              {({ isActive }: { isActive: boolean }) => (
                <NavbarItem
                  icon={subTab.icon}
                  name={subTab.name}
                  isActive={isActive}
                />
              )}
            </NavLink>
          ))}
        </div>
      )}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  subTabs: {
    marginLeft: theme.spacing.xs,
    '> *': {
      marginTop: theme.spacing.xxxs,
      // TODO: mobild
    },
  },
}));
